import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { ArrowsClockwise, CheckCircle, XCircle, Clock, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function CorpusLog() {
    const [status, setStatus] = useState(null);
    const [events, setEvents] = useState([]);
    const [refreshing, setRefreshing] = useState(false);

    const load = async () => {
        const [s, e] = await Promise.all([api.get("/corpus/status"), api.get("/corpus/events")]);
        setStatus(s.data);
        setEvents(e.data);
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 8000);
        return () => clearInterval(t);
    }, []);

    const trigger = async () => {
        setRefreshing(true);
        try {
            await api.post("/corpus/refresh");
            toast.success("Ingesta lanzada — refrescando estado cada 8 s");
            load();
        } finally {
            setRefreshing(false);
        }
    };

    return (
        <div className="fade-up">
            <PageHeader
                eyebrow="Corpus científico · log"
                title="Ingesta y revisión bibliográfica"
                lead="Cada 15 días el sistema consulta PubMed, Europe PMC y ClinicalTrials.gov para los 30+ principios activos indexados. Aquí queda el registro auditable de cada ejecución."
                actions={
                    <button onClick={trigger} disabled={refreshing || status?.is_running} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="corpus-trigger-button">
                        <ArrowsClockwise size={16} className={status?.is_running ? "animate-spin" : ""} weight="bold"/>
                        {status?.is_running ? "Ejecutándose…" : "Lanzar ingesta ahora"}
                    </button>
                }
            />
            <div className="px-8 md:px-12 py-8 max-w-6xl">
                <div className="grid sm:grid-cols-3 gap-px bg-[var(--line)] border hairline mb-10" data-testid="corpus-stats">
                    <Stat label="Principios activos" value={status?.compounds_count ?? "—"}/>
                    <Stat label="Referencias indexadas" value={status?.evidence_total ?? "—"}/>
                    <Stat label="Última ejecución" value={status?.last_event?.finished_at ? new Date(status.last_event.finished_at).toLocaleString("es-ES", { dateStyle:"short", timeStyle:"short" }) : "pendiente"} small/>
                </div>

                <div className="surface" data-testid="corpus-events-table">
                    <table className="data-table w-full">
                        <thead>
                            <tr><th>Inicio</th><th>Fin</th><th>Estado</th><th>Procesados</th><th>Nuevas refs.</th><th>Origen</th><th></th></tr>
                        </thead>
                        <tbody>
                            {events.map((e) => (
                                <tr key={e.id} data-testid={`event-row-${e.id}`} className="cursor-pointer hover:bg-[var(--cream)]">
                                    <td className="mono text-xs"><Link to={`/corpus/${e.id}`} className="block">{new Date(e.started_at).toLocaleString("es-ES", {dateStyle:"short",timeStyle:"short"})}</Link></td>
                                    <td className="mono text-xs">{e.finished_at ? new Date(e.finished_at).toLocaleString("es-ES", {dateStyle:"short",timeStyle:"short"}) : "—"}</td>
                                    <td><StatusPill status={e.status}/></td>
                                    <td className="mono">{e.compounds_processed}</td>
                                    <td className="mono"><strong>+{e.new_evidence_count}</strong></td>
                                    <td className="text-xs text-[var(--ink-2)]">{e.triggered_by}</td>
                                    <td><Link to={`/corpus/${e.id}`} className="text-[var(--sage)] inline-flex items-center gap-1 text-xs hover:underline" data-testid={`event-open-${e.id}`}>Ver estudios <ArrowRight size={12}/></Link></td>
                                </tr>
                            ))}
                            {events.length === 0 && (
                                <tr><td colSpan={7} className="text-center text-[var(--ink-3)] py-10">Sin eventos registrados</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value, small }) {
    return (
        <div className="bg-white p-6">
            <div className="label-eyebrow mb-3">{label}</div>
            <div className={small ? "font-display text-xl tracking-tight" : "stat-num"}>{value}</div>
        </div>
    );
}

function StatusPill({ status }) {
    const map = {
        success: { cls: "bg-[var(--sage)] text-white border-[var(--sage)]", Icon: CheckCircle, label: "OK" },
        failed: { cls: "bg-[var(--terra-2)] text-white border-[var(--terra-2)]", Icon: XCircle, label: "Error" },
        running: { cls: "bg-[var(--amber)] text-white border-[var(--amber)]", Icon: Clock, label: "Ejecutando" },
    };
    const s = map[status] || { cls: "", Icon: Clock, label: status };
    const Icon = s.Icon;
    return <span className={`citation-badge ${s.cls}`}><Icon size={12}/> {s.label}</span>;
}
